import { nanoid } from "nanoid";

import * as collaborationNotesApi from "@/api/collaboration-notes";
import * as pdfApi from "@/apiv2/generatePdf";
import * as alertActions from "@/redux/alerts/alerts.actions";
import { companySelector } from "@/redux/customers/customers.selectors";
import { handleError } from "@/redux/utils/error";
import { getAuthToken } from "@/utils/tokenRefreshManager";

import { SET_IS_GENERATING_PREVIEW_PDF_STATUS } from "./generatePdf.actionTypes";
import * as pdfDataTemplate from "./pdfDataTemplate";

const handleAutoAttachGeneratedPdfToCollaborations = async ({
  action,
  templateType,
  orderUid,
  purchaseOrderId,
  url,
}) => {
  if (action === "PREVIEW" || action === "EMAIL") {
    return;
  }

  if (!url) {
    console.error("Auto-attach PDF skipped: url is required", {
      templateType,
      orderUid,
      purchaseOrderId,
    });
    return;
  }

  if (!templateType) {
    console.error("Auto-attach PDF skipped: templateType is required", {
      orderUid,
      purchaseOrderId,
      url,
    });
    return;
  }

  const token = getAuthToken();

  if (!token) {
    console.error("Auto-attach PDF skipped: authentication required", {
      templateType,
      orderUid,
      purchaseOrderId,
    });
    return;
  }

  if (!orderUid && !purchaseOrderId) {
    console.error("Auto-attach PDF skipped: orderUid or purchaseOrderId is required", {
      templateType,
      url,
    });
    return;
  }

  const formData = new FormData();
  const type = templateType.toLowerCase();

  formData.set(
    "json",
    JSON.stringify({
      [purchaseOrderId ? "purchase_order_id" : "order_id"]: purchaseOrderId ?? orderUid,
      file_urls: [url],
      type,
    })
  );

  try {
    await collaborationNotesApi.saveMessageWithFiles(token, formData);
  } catch (error) {
    console.error("Failed to attach PDF to collaboration:", error, {
      templateType,
      orderUid,
      purchaseOrderId,
      url,
    });
  }
};

const handlePDFGenerationSuccess = async ({ state, action, templateType, payload, url }) => {
  const company = companySelector(state);
  const autoAttachSetting = company?.settings?.pdfSettings?.find(
    (p) => p.key === "PDF_AUTO_ATTACH_TO_COLLABORATE"
  );

  if (autoAttachSetting?.isSelected) {
    let orderUid = payload?.order?.uid;
    let purchaseOrderId;

    if (templateType === "PARTIAL_INVOICE") {
      orderUid = payload?.order?.order;
    }

    if (templateType === "PURCHASE_ORDER") {
      purchaseOrderId = payload?.order?.uid;
    }

    await handleAutoAttachGeneratedPdfToCollaborations({
      action,
      templateType,
      orderUid,
      purchaseOrderId,
      url,
    });
  }
};

export const generatePdf =
  ({
    options = {
      swapPageNumberToTheLeftSide: false,
    },
    action,
    templateType,
    fileName,
    attachments,
    payload,
    onPDFGenerated,
  }) =>
  async (dispatch, getState) => {
    try {
      const { data: defaultTemplate } = await pdfApi.getDefaultTemplateByType(templateType)();

      if (defaultTemplate) {
        const { data } = await pdfApi.postGeneratePdf({
          fileName,
          templateType: defaultTemplate.templateKey.toUpperCase(),
          templateHtmlUrl: defaultTemplate.htmlFile,
          templateData: pdfDataTemplate.getDataTemplate({ ...payload, templateType }),
          imageAttachments: attachments.images,
          pdfAttachments: attachments.pdfs,
          options,
        })();

        try {
          onPDFGenerated?.(data);
        } catch (callbackError) {
          console.error("onPDFGenerated callback failed:", callbackError);
        }

        try {
          await handlePDFGenerationSuccess({
            action,
            templateType,
            state: getState(),
            payload,
            url: data?.url,
          });
        } catch (error) {
          console.error("Auto-attach PDF to collaboration failed (best-effort):", error, {
            action,
            templateType,
            url: data?.url,
          });
        }

        return data;
      }

      await dispatch(
        alertActions.createAlertAction(
          nanoid(),
          `No "${templateType} template was found.`,
          false,
          "danger"
        )
      );
    } catch (error) {
      console.log(error);
      handleError(error, dispatch);
    }
  };

export const setIsGeneratingPreviewPdfStatus = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_GENERATING_PREVIEW_PDF_STATUS,
    payload: data,
  });
};

export const generateDeliveryRunSheetPdf =
  ({ templateType, fileName, payload }) =>
  async (dispatch) => {
    try {
      const { data: defaultTemplate } = await pdfApi.getDefaultTemplateByType(templateType)();

      if (defaultTemplate) {
        const { data } = await pdfApi.postGenerateDeliveryRunSheetPdf({
          fileName,
          templateType: defaultTemplate.templateKey.toUpperCase(),
          templateHtmlUrl: defaultTemplate.htmlFile,
          templateData: pdfDataTemplate.getDeliveryRunSheetData({ ...payload }),
        })();

        return data;
      }

      await dispatch(
        alertActions.createAlertAction(
          nanoid(),
          `No "${templateType} template was found.`,
          false,
          "danger"
        )
      );
    } catch (error) {
      console.log(error);
      handleError(error, dispatch);
    }
  };

// Invoice, Quote, and Proforma Invoice
export const generatePdfData =
  ({ orderUid, templateType, payload }) =>
  async (dispatch) => {
    try {
      const { imageAttachmentUids, pdfAttachmentUids, isPreview, isEmail, ...rest } = payload;
      const formData = {
        order_id: orderUid,
        attachment_images: imageAttachmentUids,
        attachment_pdfs: pdfAttachmentUids,
        is_preview: isPreview,
        is_email: isEmail,
        ...rest,
      };
      const apiMap = {
        INVOICE: pdfApi.postGenerateInvoice,
        QUOTE: pdfApi.postGenerateQuote,
        PROFORMA_INVOICE: pdfApi.postGenerateProformaInvoice,
      };

      const { data } = await apiMap[templateType](formData)();

      // FAC-6082: a regenerated invoice is kept even when the accounting sync fails;
      // the backend reports why in accounting_errors. Warn, don't block.
      if (data?.accountingErrors?.length) {
        dispatch(
          alertActions.createAlertAction(
            nanoid(),
            `Invoice saved, but it wasn't updated in your accounting package: ${data.accountingErrors.join(" ")}`,
            false,
            "warning"
          )
        );
      }

      return data;
    } catch (error) {
      handleError(error, dispatch);
    }
  };

// Work Order Pdf
export const generateWorkOrder =
  ({ orderUid, payload = {} } = {}) =>
  async (dispatch) => {
    try {
      const formData = {
        order_id: orderUid,
        custom_products: payload?.products ?? undefined,
        custom_drawings: payload?.drawings ?? undefined,
      };

      const { data } = await pdfApi.postGenerateWorkOrder(formData)();

      return data;
    } catch (error) {
      console.log(error);
      handleError(error, dispatch);
    }
  };

export const generateDeliveryDocket =
  ({ orderUid, payload = {} } = {}) =>
  async (dispatch) => {
    try {
      const formData = {
        order_id: orderUid,
        custom_products: payload?.products ?? undefined,
        custom_drawings: payload?.drawings ?? undefined,
      };

      const { data } = await pdfApi.postGenerateDeliveryDocket(formData)();

      return data;
    } catch (error) {
      console.log(error);
      handleError(error, dispatch);
    }
  };

export const generatePartialInvoice =
  ({ orderUid, payload }) =>
  async (dispatch) => {
    try {
      const formData = {
        order_id: orderUid,
        attachment_images: payload.imageAttachmentUids,
        attachment_pdfs: payload.pdfAttachmentUids,
        invoiced_date: payload.invoicedDate,
        is_preview: payload.isPreview,
        is_email: payload.isEmail,
        invoice_type: payload.invoiceType,
        value: payload.value,
        items: payload.items,
        delivery_fee:
          payload.deliveryFee && parseFloat(payload.deliveryFee) > 0
            ? payload.deliveryFee
            : undefined,
        partial_invoice_id: payload.partialInvoiceId || undefined,
        invoice_display_mode: payload.invoiceDisplayMode,
        // Grouped mode carries its bucket labels, one-liner its description; empty
        // for full breakdown. Keys are snake-cased by the api layer.
        ...(payload.displayModeExtras ?? {}),
      };

      const { data } = await pdfApi.postGeneratePartialInvoice(formData)();
      return data;
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const generatePurchaseOrder =
  ({ purchaseOrderUid, payload }) =>
  async (dispatch) => {
    try {
      const { imageAttachmentUids, pdfAttachmentUids, isPreview, isEmail, ...rest } = payload;
      const formData = {
        purchase_order_id: purchaseOrderUid,
        attachment_images: imageAttachmentUids,
        attachment_pdfs: pdfAttachmentUids,
        is_preview: isPreview,
        is_email: isEmail,
        ...rest,
      };
      const { data } = await pdfApi.postGeneratePurchaseOrder(formData)();
      return data;
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const generateRunSheet =
  ({ startDate = null, endDate = null, vehicleUids = [] } = {}) =>
  async (dispatch) => {
    try {
      const payload = {
        run_date_start: startDate,
        run_date_end: endDate,
        vehicles: vehicleUids,
      };

      const { data } = await pdfApi.postGenerateRunSheet(payload)();
      return data;
    } catch (error) {
      console.log(error);
      handleError(error, dispatch);
    }
  };

export const emailPdf =
  ({ customerUid, orderUid, type, payload }) =>
  async (dispatch) => {
    try {
      const formData = {
        type,
        attachments: payload.attachmentUids,
        emails: payload.contactsToEmail,
        pdf_date: payload.pdfDate,
        custom_email_message: payload.customEmailMessage,
        pdf_url: payload.pdfUrl,
        partial_invoice: payload.partialInvoiceId || undefined,
      };

      const { data } = await pdfApi.postEmailPdf({ customerUid, orderUid, data: formData })();
      return data;
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const emailPurchaseOrderPdf =
  ({ purchaseOrderUid, payload }) =>
  async (dispatch) => {
    try {
      const formData = {
        attachments: payload.attachmentUids,
        emails: payload.contactsToEmail,
        pdf_date: payload.pdfDate,
        custom_email_message: payload.customEmailMessage,
        pdf_url: payload.pdfUrl,
      };

      const { data } = await pdfApi.postPurchaseOrderEmailPdf({
        purchaseOrderUid,
        data: formData,
      })();
      return data;
    } catch (error) {
      handleError(error, dispatch);
    }
  };
export default {};
